import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Search, MapPin, FileText, BarChart3, Check, Star, Loader2, Send, ArrowRight } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { ChatWidget } from "@/components/landing/ChatWidget";
import { API } from "@/lib/site";
import { useEffect } from "react";

const PACKAGES = [
  {
    id: "seo-package-starter",
    name: "SEO Starter",
    price: 490,
    features: ["Full technical SEO audit & fixes", "5 target keywords", "Google Business Profile setup", "On-page optimization", "Monthly ranking report"],
    bestFor: "Local businesses getting found for the first time",
  },
  {
    id: "seo-package-growth",
    name: "SEO Growth",
    price: 990,
    popular: true,
    features: ["Everything in Starter", "15 target keywords", "4 SEO content pieces / month", "Local SEO & maps optimization", "Competitor tracking", "Monthly strategy call"],
    bestFor: "Growing businesses ready to outrank competitors",
  },
  {
    id: "seo-package-domination",
    name: "SEO Domination",
    price: 1990,
    features: ["Everything in Growth", "40 target keywords", "8 SEO content pieces / month", "Authority link building", "Conversion tracking & CRO", "Quarterly growth strategy"],
    bestFor: "Businesses serious about owning page one",
  },
];

const PILLARS = [
  { icon: Search, title: "Technical SEO", desc: "Site speed, crawlability, schema, and Core Web Vitals — the foundation Google rewards." },
  { icon: MapPin, title: "Local Search", desc: "Google Business Profile, maps rankings, and reviews so nearby customers find you first." },
  { icon: FileText, title: "Content That Ranks", desc: "Keyword-driven content engineered to answer real searches and convert readers into customers." },
  { icon: BarChart3, title: "Transparent Reporting", desc: "Monthly rank tracking, traffic, and lead reports — you always know exactly what's working." },
];

const inputCls =
  "w-full px-4 py-3.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary transition-shadow";

export default function SEOPage() {
  const [dark, setDark] = useState(() => localStorage.getItem("wynova-theme") === "dark");
  const [form, setForm] = useState({ name: "", email: "", website: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("wynova-theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    document.title = "SEO Services & Packages — Wynova Systems";
  }, []);

  const submitAudit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        company: "",
        service_interest: "Free SEO Audit",
        budget_range: "Not sure yet",
        message: `Free SEO audit requested for website: ${form.website}`,
      });
      toast.success("Audit requested — we'll analyse your site and email the findings within 2 business days.");
      setForm({ name: "", email: "", website: "" });
    } catch {
      toast.error("Submission failed. Please try again or call 0401 510 499.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="seo-page" className="min-h-screen bg-background text-foreground">
      <Header dark={dark} onToggleTheme={() => setDark((d) => !d)} basePath="/" />
      <main>
        <section className="relative overflow-hidden technical-grid pt-32 sm:pt-40 pb-20">
          <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-primary/15 blur-3xl pointer-events-none" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative">
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                      className="font-semibold text-xs uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400 mb-6">
              SEO & Search Growth
            </motion.p>
            <h1 className="font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
              <span className="block overflow-hidden pb-1">
                <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
                  Get Found on Google.
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-1">
                <motion.span className="block text-primary" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}>
                  Stay There.
                </motion.span>
              </span>
            </h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
                      className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600 dark:text-slate-300">
              Technical SEO, local search, and content that compounds into long-term organic growth — so customers find you before they find your competitors.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.55 }}
                        className="mt-10 flex flex-wrap gap-4">
              <a href="#free-audit" data-testid="seo-hero-audit-cta"
                 className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-primary text-white font-semibold shadow-xl shadow-blue-600/25 hover:bg-blue-700 transition-colors">
                Get My Free Website Audit <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
              <a href="#packages" data-testid="seo-hero-packages-cta"
                 className="inline-flex items-center gap-2 px-7 py-4 rounded-full border-2 border-slate-900/80 dark:border-slate-200/80 font-semibold hover:bg-slate-900 hover:text-white dark:hover:bg-white dark:hover:text-slate-900 transition-colors">
                See SEO Packages
              </a>
            </motion.div>
          </div>
        </section>

        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <h2 className="font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl max-w-2xl">What Our SEO Covers</h2>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {PILLARS.map((p, i) => (
                <motion.div key={p.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
                            className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-7 hover:border-primary/50 transition-colors">
                  <span className="inline-flex p-3 rounded-2xl bg-primary/10 text-primary"><p.icon className="w-6 h-6" /></span>
                  <h3 className="mt-5 font-bold text-lg tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{p.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section id="packages" className="py-20 bg-slate-100/70 dark:bg-slate-900/40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <div className="max-w-2xl mb-14">
              <p className="font-semibold text-xs uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400 mb-4">SEO Packages</p>
              <h2 className="font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">Plans That Grow With You</h2>
              <p className="mt-4 text-base sm:text-lg text-slate-600 dark:text-slate-300">No lock-in contracts. Cancel anytime. Every package starts with a free audit.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
              {PACKAGES.map((p, i) => (
                <motion.div key={p.id} data-testid={p.id}
                            initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: i * 0.12 }}
                            className={`relative rounded-3xl border p-6 sm:p-8 flex flex-col transition-all duration-300 hover:shadow-2xl ${
                              p.popular
                                ? "bg-slate-950 text-white border-primary shadow-xl shadow-blue-600/20 lg:-mt-4 lg:mb-[-1rem]"
                                : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-primary/50"
                            }`}>
                  {p.popular && (
                    <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary text-white text-xs font-bold uppercase tracking-wider">
                      <Star className="w-3 h-3 fill-current" /> Most Popular
                    </span>
                  )}
                  <h3 className="font-bold text-lg sm:text-xl">{p.name}</h3>
                  <div className="mt-4 flex items-end gap-1">
                    <span className="font-black text-4xl sm:text-5xl tracking-tight">${p.price}</span>
                    <span className={`text-sm mb-1.5 ${p.popular ? "text-slate-400" : "text-slate-500"}`}>/mo</span>
                  </div>
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className={`flex items-center gap-2 text-sm ${p.popular ? "text-slate-300" : "text-slate-600 dark:text-slate-300"}`}>
                        <Check className={`w-4 h-4 shrink-0 ${p.popular ? "text-cyan-400" : "text-primary"}`} /> {f}
                      </li>
                    ))}
                  </ul>
                  <p className={`mt-6 text-xs ${p.popular ? "text-slate-400" : "text-slate-500"}`}>Best for: {p.bestFor}</p>
                  <a href="#free-audit" data-testid={`${p.id}-cta`}
                     className={`mt-6 inline-flex justify-center px-6 py-3.5 rounded-full font-semibold text-sm transition-colors ${
                       p.popular ? "bg-primary text-white hover:bg-blue-600"
                                 : "border-2 border-slate-900/80 dark:border-slate-200/70 hover:bg-slate-900 hover:text-white dark:hover:bg-white dark:hover:text-slate-900"
                     }`}>
                    Start With a Free Audit
                  </a>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section id="free-audit" className="py-20 lg:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-2 gap-14 items-start">
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <p className="font-semibold text-xs uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400 mb-4">Free Website Audit</p>
              <h2 className="font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">Find Out Why You're Not Ranking</h2>
              <p className="mt-4 text-base sm:text-lg text-slate-600 dark:text-slate-300 max-w-md">
                Drop your website below and our team will personally analyse it — speed, keywords, technical issues, and quick wins — then email you a clear action plan. Free, no strings.
              </p>
              <ul className="mt-10 space-y-5">
                {[
                  ["01", "We crawl your site and check 50+ ranking factors."],
                  ["02", "You get a plain-English report within 2 business days."],
                  ["03", "We walk you through it on a free 15-minute call."],
                ].map(([n, t]) => (
                  <li key={n} className="flex items-start gap-4">
                    <span className="font-display font-black text-primary/30 text-2xl leading-none">{n}</span>
                    <span className="text-sm sm:text-base text-slate-600 dark:text-slate-300 pt-0.5">{t}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.form onSubmit={submitAudit} data-testid="seo-audit-form"
                         initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                         viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }}
                         className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-10 shadow-xl shadow-slate-900/5">
              <input required minLength={2} placeholder="Your name *" value={form.name} data-testid="seo-audit-name"
                     onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              <input required type="email" placeholder="Email address *" value={form.email} data-testid="seo-audit-email"
                     onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inputCls} mt-5`} />
              <input required minLength={4} placeholder="Your website (e.g. yourbusiness.com) *" value={form.website} data-testid="seo-audit-website"
                     onChange={(e) => setForm({ ...form, website: e.target.value })} className={`${inputCls} mt-5`} />
              <button type="submit" disabled={sending} data-testid="seo-audit-submit"
                      className="mt-7 w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-primary text-white font-semibold shadow-xl shadow-blue-600/25 hover:bg-blue-700 disabled:opacity-60 transition-colors">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? "Requesting…" : "Request My Free Audit"}
              </button>
              <p className="mt-4 text-xs text-slate-400 text-center">Free, no obligation. We'll never share your details.</p>
            </motion.form>
          </div>
        </section>
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
}
